import { CardContent, CircularProgress, Grid, Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getUserByUuId } from '../../../data/api/users/UserRoutes';
import useIndex from '../../../hooks/useIndex';
import { useNavigate } from 'react-router-dom';
import validErrors from '../../../hooks/validErrors';
import UserTabsOption from './tabs/UserTabsOption';

const UserProfile = ({ uuid }) => {
    const serverUrl = process.env.REACT_APP_BACK_URL;
    const { token } = useIndex();
    const navigate = useNavigate();

    const [user, setUser] = useState([]);
    const [panel, setPanel] = useState('1');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);

        try {
            const { data } = await getUserByUuId(token, uuid);
            setUser(data?.data);
        } catch (error) {
            validErrors(error);
        } finally {
            setLoading(false);
        }
    };

    const isCompanyUser = String(user?.role_id) === '4';

    const menuItems = isCompanyUser
        ? [
            { id: '1', label: 'Convenios', icon: 'fa-solid fa-handshake' },
            { id: '2', label: 'Colecciones', icon: 'fa-solid fa-list' },
            { id: '3', label: 'Estadísticas', icon: 'fa-solid fa-chart-column' },
            { id: '99', label: 'Configuración', icon: 'fa-solid fa-gear' },
        ]
        : [
            { id: '1', label: 'Historial', icon: 'fa-solid fa-clock-rotate-left' },
            { id: '2', label: 'Estadísticas', icon: 'fa-solid fa-chart-column' },
            { id: '99', label: 'Configuración', icon: 'fa-solid fa-gear' },
        ];

    return (
        <PageWrapper>
            <TopBar>
                <BackButton onClick={() => navigate('/ganacol/dashboard/users')}>
                    <ArrowBackIcon />
                    <span>Volver</span>
                </BackButton>

                <Box>
                    <Typography className="title">
                        Perfil de usuario
                    </Typography>
                    <Typography className="subtitle">
                        Consulta historial, estadísticas, convenios y configuración.
                    </Typography>
                </Box>
            </TopBar>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4} lg={3}>
                    <ProfileCard>
                        {loading ? (
                            <LoadingProfile>
                                <CircularProgress size={30} sx={{ color: '#d6aa3a' }} />
                                <Typography>Cargando usuario...</Typography>
                            </LoadingProfile>
                        ) : (
                            <>
                                <AvatarWrap>
                                    <img
                                        src={serverUrl + user?.avatar}
                                        alt={user?.name ?? 'user-avatar'}
                                    />
                                </AvatarWrap>

                                <Typography className="user-name">
                                    {user?.name ?? 'Usuario'}
                                </Typography>

                                <RoleBadge>
                                    {user?.role ?? 'Sin rol'}
                                </RoleBadge>

                                <UserDescription>
                                    Registrado el <strong>{user?.created_at ?? '-'}</strong>, lleva{' '}
                                    <strong>{user?.active_days ?? 0}</strong> días en el sistema y ha realizado{' '}
                                    <strong>{user?.games_count ?? 0}</strong> juegos.
                                </UserDescription>

                                <StatsMiniGrid>
                                    <StatMini>
                                        <strong>{user?.active_days ?? 0}</strong>
                                        <span>Días</span>
                                    </StatMini>

                                    <StatMini>
                                        <strong>{user?.games_count ?? 0}</strong>
                                        <span>Juegos</span>
                                    </StatMini>
                                </StatsMiniGrid>

                                <MenuList>
                                    {menuItems.map((item) => (
                                        <MenuItem
                                            key={item.id}
                                            $active={panel === item.id}
                                            onClick={() => setPanel(item.id)}
                                        >
                                            <i className={item.icon}></i>
                                            <span>{item.label}</span>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </>
                        )}
                    </ProfileCard>
                </Grid>

                <Grid item xs={12} md={8} lg={9}>
                    <ContentCard>
                        <CardContent>
                            <UserTabsOption
                                loading={loading}
                                panel={panel}
                                user={user}
                                loadData={loadData}
                            />
                        </CardContent>
                    </ContentCard>
                </Grid>
            </Grid>
        </PageWrapper>
    );
};

export default UserProfile;

const PageWrapper = styled(Box)`
    min-height: 100%;
    background: #f5f7fb;
    padding: 1rem;

    @media (max-width: 600px) {
        padding: 0.75rem;
    }
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 0.85rem 1rem;
    border-radius: 18px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.25);
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.06);

    .title {
        color: #10285f;
        font-size: 1.25rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        margin-top: 0.15rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.86rem;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.65rem;
    }
`;

const BackButton = styled.button`
    border: none;
    min-width: 42px;
    height: 38px;
    padding: 0 0.9rem;
    border-radius: 12px;
    background: rgba(16, 40, 95, 0.06);
    color: #10285f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    cursor: pointer;
    font-weight: 900;
    transition: 0.2s ease;

    svg {
        font-size: 1.15rem;
    }

    &:hover {
        background: rgba(214, 170, 58, 0.16);
        color: #8a6500;
        transform: translateX(-2px);
    }
`;

const ProfileCard = styled.div`
    border-radius: 22px;
    padding: 1.2rem;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08);
    text-align: center;
    position: sticky;
    top: 1rem;

    @media (max-width: 900px) {
        position: static;
    }
`;

const LoadingProfile = styled.div`
    min-height: 280px;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    align-items: center;
    justify-content: center;
    color: #10285f;
    font-weight: 700;
`;

const AvatarWrap = styled.div`
    width: 128px;
    height: 128px;
    margin: 0 auto 0.9rem;
    border-radius: 50%;
    padding: 4px;
    overflow: hidden;
    background: rgba(214, 170, 58, 0.16);
    border: 3px solid rgba(214, 170, 58, 0.7);
    box-shadow: 0 10px 22px rgba(15, 34, 77, 0.12);

    img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
        border-radius: 50%;
        background: #fff;
    }

    @media (max-width: 600px) {
        width: 104px;
        height: 104px;
    }
`;

const RoleBadge = styled.span`
    display: inline-flex;
    margin-bottom: 0.8rem;
    padding: 0.35rem 0.8rem;
    border-radius: 999px;
    background: rgba(214, 170, 58, 0.18);
    border: 1px solid rgba(214, 170, 58, 0.34);
    color: #10285f;
    font-size: 0.78rem;
    font-weight: 900;
    text-transform: uppercase;
`;

const UserDescription = styled.p`
    margin: 0 auto 1rem;
    color: rgba(15, 34, 77, 0.65);
    font-size: 0.88rem;
    line-height: 1.55;
    max-width: 280px;

    strong {
        color: #10285f;
        font-weight: 900;
    }
`;

const StatsMiniGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.7rem;
    margin-bottom: 1rem;
`;

const StatMini = styled.div`
    padding: 0.7rem;
    border-radius: 16px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);

    strong {
        display: block;
        color: #10285f;
        font-size: 1.1rem;
        font-weight: 900;
    }

    span {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.78rem;
        font-weight: 700;
    }
`;

const MenuList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    text-align: left;
`;

const MenuItem = styled.button`
    width: 100%;
    border: none;
    border-radius: 14px;
    padding: 0.75rem 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    transition: 0.2s ease;
    text-align: left;

    background: ${({ $active }) =>
        $active ? 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)' : 'transparent'};

    color: ${({ $active }) => ($active ? '#fff' : '#10285f')};
    font-size: 0.9rem;
    font-weight: 900;

    i {
        width: 18px;
        color: ${({ $active }) => ($active ? '#f3cd61' : '#d6aa3a')};
    }

    &:hover {
        background: ${({ $active }) =>
        $active ? 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)' : 'rgba(214, 170, 58, 0.12)'};
        transform: translateX(2px);
    }
`;

const ContentCard = styled.div`
    min-height: 480px;
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08);
    overflow: hidden;
`;

const UserCard = styled.div`
    .user-name {
        color: #10285f;
        font-size: 1.35rem;
        font-weight: 900;
        line-height: 1.15;
        margin-bottom: 0.45rem;
    }
`;